export const COLUMNS = {
  uniqueExternalId: 'Unique External Id',
  deviceName: 'Device Name',
  currentTags: 'Current Tags',
  deploymentRingName: 'Deployment Ring Name',
  deviceStatus: 'Device Status',
  addTags: 'Add Tags',
  removeTags: 'Remove Tags',
};

export const REQUIRED_COLUMNS = [COLUMNS.uniqueExternalId];
export const REFERENCE_ONLY_COLUMNS = [COLUMNS.deviceName, COLUMNS.currentTags];
export const UPDATABLE_COLUMNS = [
  COLUMNS.deploymentRingName,
  COLUMNS.deviceStatus,
  COLUMNS.addTags,
  COLUMNS.removeTags,
];

export const CHUNK_SIZE = 100;
const MAX_LISTED_ROWS = 5;
const USE_TEMPLATE = 'Please use the template.';

const listRows = rowNumbers => {
  const shown = rowNumbers.slice(0, MAX_LISTED_ROWS).join(', ');
  const extra = rowNumbers.length - MAX_LISTED_ROWS;
  return extra > 0 ? `${shown} ...and ${extra} more` : shown;
};

export const chunkRows = (rows, size = CHUNK_SIZE) => {
  const chunks = [];
  for (let index = 0; index < rows.length; index += size) {
    chunks.push(rows.slice(index, index + size));
  }
  return chunks;
};

// Checks the headers and the Unique External Id cells of a parsed file. `rows` are
// { rowNumber, values } where rowNumber is the line in the file (header is line 1).
// Returns an array of human readable errors, empty when the file is in the expected format.
export const validateBulkUpdateFile = (headers, rows) => {
  const trimmedHeaders = headers.map(header => (header || '').trim());
  const known = [...REQUIRED_COLUMNS, ...REFERENCE_ONLY_COLUMNS, ...UPDATABLE_COLUMNS];

  const missing = REQUIRED_COLUMNS.filter(column => !trimmedHeaders.includes(column));
  if (missing.length > 0) {
    return [`Missing required columns: ${missing.join(', ')}. ${USE_TEMPLATE}`];
  }

  const errors = [];
  const invalid = trimmedHeaders.filter(header => header && !known.includes(header));
  if (invalid.length > 0) {
    errors.push(`Invalid columns: ${invalid.join(', ')}. ${USE_TEMPLATE}`);
  }
  const duplicateHeaders = trimmedHeaders.filter(
    (header, index) => header && trimmedHeaders.indexOf(header) !== index,
  );
  if (duplicateHeaders.length > 0) {
    errors.push(`Duplicate columns: ${[...new Set(duplicateHeaders)].join(', ')}.`);
  }
  if (!UPDATABLE_COLUMNS.some(column => trimmedHeaders.includes(column))) {
    errors.push(`No columns to update. Include at least one of: ${UPDATABLE_COLUMNS.join(', ')}.`);
  }
  if (rows.length === 0) {
    errors.push('The file does not contain any rows.');
    return errors;
  }

  const idIndex = trimmedHeaders.indexOf(COLUMNS.uniqueExternalId);
  const blankRows = [];
  const firstSeen = new Map();
  const duplicateRows = [];
  rows.forEach(({ rowNumber, values }) => {
    const id = (values[idIndex] || '').trim();
    if (!id) {
      blankRows.push(rowNumber);
    } else if (firstSeen.has(id)) {
      duplicateRows.push(rowNumber);
    } else {
      firstSeen.set(id, rowNumber);
    }
  });
  if (blankRows.length > 0) {
    errors.push(`${COLUMNS.uniqueExternalId} is blank on row(s): ${listRows(blankRows)}.`);
  }
  if (duplicateRows.length > 0) {
    errors.push(`${COLUMNS.uniqueExternalId} is repeated on row(s): ${listRows(duplicateRows)}.`);
  }
  return errors;
};

export const TAG_SEPARATOR = '|';
export const VALID_STATUSES = ['Approved', 'Revoked', 'Archived', 'Unknown'];

// Splits an Add Tags / Remove Tags cell into distinct, trimmed tag names.
export const splitTags = value => {
  const seen = new Set();
  return (value || '')
    .split(TAG_SEPARATOR)
    .map(name => name.trim())
    .filter(name => {
      const key = name.toLowerCase();
      if (!name || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

// Returns the canonical status for a cell, or null when it is not one the update accepts.
export const parseStatus = value => {
  const text = (value || '').trim().toLowerCase();
  if (text === 'no status') return 'Unknown';
  return VALID_STATUSES.find(status => status.toLowerCase() === text) || null;
};

// A parsed file line as an object: the updatable cells by name, plus what is needed to write the
// line back out (and report on it) later. Keys starting with "_" are never sent to the server.
export const toRow = (headers, values, rowNumber) => {
  const cell = column => {
    const index = headers.findIndex(header => (header || '').trim() === column);
    return index === -1 ? '' : (values[index] || '').trim();
  };
  return {
    _rowNumber: rowNumber,
    _originalRow: values,
    uniqueExternalId: cell(COLUMNS.uniqueExternalId),
    deploymentRingName: cell(COLUMNS.deploymentRingName),
    deviceStatus: cell(COLUMNS.deviceStatus),
    addTags: cell(COLUMNS.addTags),
    removeTags: cell(COLUMNS.removeTags),
  };
};

export const toApiRow = row => ({
  uniqueExternalId: row.uniqueExternalId,
  deploymentRingName: row.deploymentRingName,
  deviceStatus: row.deviceStatus,
  addTags: row.addTags,
  removeTags: row.removeTags,
});
